import React, { useState, useEffect } from "react";
import axiosInstance from "../../utils/axiosInstance";
import SuccessMessage from "../../Components/shared/SuccessMessage";

const HospitalModel = ({
  isOpen,
  onClose,
  hospital,
  refreshHospitals,
  showSuccessMessage,
}) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");
  const [website, setWebsite] = useState("");
  const [successMessage, setSuccessMessage] = useState(""); // ✅ State for success message
  const [isError, setIsError] = useState(false);

  useEffect(() => {
    if (hospital) {
      setName(hospital.name);
      setDescription(hospital.description);
      setPhoneNumber(hospital.phoneNumber || "");
      setEmail(hospital.email || "");
      setAddress(hospital.address || "");
      setCity(hospital.city || "");
      setState(hospital.state || "");
      setPincode(hospital.pincode || "");
      setWebsite(hospital.website || "");
      setPreview(
        hospital.image
          ? `${process.env.REACT_APP_API_URL}/${hospital.image}`
          : ""
      );
    } else {
      setName("");
      setDescription("");
      setPhoneNumber("");
      setEmail("");
      setAddress("");
      setCity("");
      setState("");
      setPincode("");
      setWebsite("");
      setImage(null);
      setPreview("");
    }
  }, [hospital]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];

    // Validate PNG only
    if (file && file.type !== "image/png") {
      showSuccessMessage(
        "Only PNG files are supported! Please upload a PNG image.",
        true
      );
      e.target.value = ""; // Clear the file input
      return;
    }

    setImage(file);
    setPreview(URL.createObjectURL(file));
  };

  const closeMessage = () => {
    setSuccessMessage(""); // Clears the message
    setIsError(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData();
    formData.append("name", name);
    formData.append("description", description);
    formData.append("phoneNumber", phoneNumber);
    formData.append("email", email);
    formData.append("address", address);
    formData.append("city", city);
    formData.append("state", state);
    formData.append("pincode", pincode);
    formData.append("website", website);
    if (image) formData.append("image", image);

    try {
      if (hospital) {
        await axiosInstance.put(`/hospitals/${hospital.id}`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessMessage("Hospital updated successfully! ✅");
      } else {
        await axiosInstance.post("/hospitals", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        showSuccessMessage("Hospital added successfully! ✅");
      }

      // Close modal immediately
      onClose();
      refreshHospitals();
    } catch (error) {
      console.error("Error saving hospital:", error);
      showSuccessMessage("Error saving hospital! ❌", true);
    }
  };

  return isOpen ? (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 overflow-y-auto">
      <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl m-4 max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-bold mb-4 text-gray-800">
          {hospital ? "Edit Hospital" : "Add Hospital"}
        </h3>

        {/* ✅ SUCCESS MESSAGE FIXED */}
        {successMessage && (
          <SuccessMessage
            message={successMessage}
            isError={isError}
            onClose={closeMessage}
          />
        )}

        <form onSubmit={handleSubmit} encType="multipart/form-data">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Hospital Name */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hospital Name *
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="text"
                placeholder="Enter hospital name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            {/* Phone Number */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Phone Number
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="tel"
                placeholder="Enter phone number"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                maxLength="15"
              />
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            {/* Address */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Address
              </label>
              <textarea
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter full address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                rows="2"
              />
            </div>

            {/* City */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="text"
                placeholder="Enter city"
                value={city}
                onChange={(e) => setCity(e.target.value)}
              />
            </div>

            {/* State */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="text"
                placeholder="Enter state"
                value={state}
                onChange={(e) => setState(e.target.value)}
              />
            </div>

            {/* Pincode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Pincode
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="text"
                placeholder="Enter pincode"
                value={pincode}
                onChange={(e) => setPincode(e.target.value)}
                maxLength="6"
              />
            </div>

            {/* Website */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Website
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="url"
                placeholder="https://example.com"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter hospital description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows="3"
              />
            </div>

            {/* LOGO UPLOAD FIELD */}
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Hospital Logo (PNG only)
              </label>
              <input
                className="border border-gray-300 rounded-md p-2 w-full focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                type="file"
                accept=".png,image/png"
                onChange={handleImageChange}
              />
              <p className="text-xs text-gray-500 mt-1">
                Only PNG format is supported
              </p>
            </div>

            {/* LOGO PREVIEW */}
            {preview && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Logo Preview
                </label>
                <img
                  src={preview}
                  alt="Hospital Logo Preview"
                  className="w-full h-48 object-cover rounded-md border border-gray-200"
                />
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 mt-6">
            <button
              type="submit"
              className="flex-1 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition font-medium"
            >
              {hospital ? "Update Hospital" : "Add Hospital"}
            </button>
            <button
              type="button"
              className="flex-1 bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600 transition font-medium"
              onClick={onClose}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  ) : null;
};

export default HospitalModel;
